import { BaseChangeLog } from '@models/next/ChangeLog';
import { DeliveryRequestDateType } from '@models/next/DeliveryRequest';
import { z } from 'zod';

export const BaseDeliveryRequestLog = BaseChangeLog.extend({
  delivery_request: z.number(),
});

export const DeliveryRequestLog = z.discriminatedUnion('edited_field', [
  // self start
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('delivery_request_no'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_date'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('delivery_date'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('delivery_date_type'),
    before_value: DeliveryRequestDateType,
    after_value: DeliveryRequestDateType,
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('truck_type'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('truck_count'),
    before_value: z.number(),
    after_value: z.number(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('memo'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('do_less_no'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('move_out_no'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  // self end
  // pickup_address start
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.address'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.delivery_destination'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.fax'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.principal_name'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.repository'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pickup_address.tel'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  // pickup_address end
  // booking inquiry start
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.d2d_no'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.consignee.english_name'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.english_name'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.company_name'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.zipcode'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.address1'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.address2'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.principal_name'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('booking_inquiry.receiver.phone'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  // booking inquiry end
  // pre alert start
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pre_alert.quantity'),
    before_value: z.number(),
    after_value: z.number(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pre_alert.packages'),
    before_value: z.number(),
    after_value: z.number(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pre_alert.measurement'),
    before_value: z.number(),
    after_value: z.number(),
  }),
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pre_alert.gross_weight'),
    before_value: z.number(),
    after_value: z.number(),
  }),
  // pre alert end
  // vessel schedule start
  BaseDeliveryRequestLog.extend({
    edited_field: z.literal('pre_alert.vessel_schedule.pod'),
    before_value: z.string(),
    after_value: z.string(),
  }),
  // vessle schedule end
]);

export type DeliveryRequestLog = z.infer<typeof DeliveryRequestLog>;

export type DeliveryRequestLogFormatters = {
  [K in DeliveryRequestLog['edited_field']]?: (
    value: Extract<DeliveryRequestLog, { edited_field: K }>['before_value'],
  ) => string;
};

export const exampleDeliveryRequestLog: DeliveryRequestLog = {
  id: 1,
  editor: 'Yaju',
  label: '納品先 - 担当者名',
  created_at: '2023-03-24T05:35:04.885Z',
  updated_at: '2023-03-24T05:35:04.885Z',
  detail: '',
  delivery_request: 1,
  edited_field: 'booking_inquiry.receiver.principal_name',
  before_value: 'TDN',
  after_value: 'MUR',
};

export const exampleDeliveryRequestLogs: DeliveryRequestLog[] = [
  exampleDeliveryRequestLog,
  {
    ...exampleDeliveryRequestLog,
    id: 2,
    label: '集荷日',
    edited_field: 'pickup_date',
    before_value: '2022-11-11',
    after_value: '2022-11-22',
  },
  {
    ...exampleDeliveryRequestLog,
    id: 3,
    label: '納品日',
    edited_field: 'delivery_date',
    before_value: '2022-11-11',
    after_value: '2022-11-22',
  },
];
